import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  IconButton,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  useTheme,
  Avatar,
  Badge,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  Close,
  Search,
  FilterList,
  Visibility,
  Edit,
  Download,
  Share,
  Assignment,
  Person,
  Folder,
  Description,
  Image,
  VideoFile,
  AudioFile,
  Archive,
  Email,
  PictureAsPdf,
  TextFields,
  TableChart,
  InsertDriveFile,
  ViewModule,
  ViewList,
  Sort,
} from '@mui/icons-material';

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  documentType: string;
  category: string;
  tags: string[] | string;
  isConfidential: boolean;
  isPublic: boolean;
  isTemplate: boolean;
  version: number;
  caseId: string | null;
  clientId: string | null;
  assignedTo: string[];
  uploadedBy: string;
  createdAt: string | null;
  updatedAt: string | null;
}

interface DocumentGalleryProps {
  open: boolean;
  onClose: () => void;
  documents: Document[];
  onDocumentClick: (document: Document) => void;
  onDocumentEdit: (document: Document) => void;
  onDocumentDownload: (documentId: string, fileName: string) => void;
}

const DocumentGallery: React.FC<DocumentGalleryProps> = ({
  open,
  onClose,
  documents,
  onDocumentClick,
  onDocumentEdit,
  onDocumentDownload,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size' | 'type'>('name');

  const getDocumentTypeIcon = (documentType: string) => {
    switch (documentType) {
      case 'image':
        return <Image />;
      case 'video':
        return <VideoFile />;
      case 'audio':
        return <AudioFile />;
      case 'archive':
        return <Archive />;
      case 'email_attachment':
        return <Email />;
      case 'pdf':
        return <PictureAsPdf />;
      case 'text':
        return <TextFields />;
      case 'spreadsheet':
        return <TableChart />;
      default:
        return <Description />;
    }
  };

  const getDocumentTypeColor = (documentType: string) => {
    switch (documentType) {
      case 'pleading':
        return 'primary';
      case 'evidence':
        return 'error';
      case 'correspondence':
        return 'info';
      case 'contract':
        return 'success';
      case 'invoice':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !filterType || doc.documentType === filterType;
    return matchesSearch && matchesType;
  });

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.title.localeCompare(b.title);
      case 'date':
        return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
      case 'size':
        return b.fileSize - a.fileSize;
      case 'type':
        return a.documentType.localeCompare(b.documentType);
      default:
        return 0;
    }
  });

  const getDocumentPreview = (document: Document) => {
    // This would generate a preview based on document type
    if (document.mimeType.startsWith('image/')) {
      return (
        <CardMedia
          component="img"
          height="140"
          image={`/api/documents/${document.id}/preview`}
          alt={document.title}
          sx={{ objectFit: 'cover' }}
        />
      );
    }
    
    return (
      <Box
        sx={{
          height: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.grey[100],
        }}
      >
        <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
          {getDocumentTypeIcon(document.documentType)}
        </Avatar>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Document Gallery ({documents.length} documents)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={viewMode === 'grid' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<ViewModule />}
              onClick={() => setViewMode('grid')}
            >
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<ViewList />}
              onClick={() => setViewMode('list')}
            >
              List
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Document Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="pleading">Pleading</MenuItem>
                  <MenuItem value="discovery">Discovery</MenuItem>
                  <MenuItem value="correspondence">Correspondence</MenuItem>
                  <MenuItem value="evidence">Evidence</MenuItem>
                  <MenuItem value="court_filing">Court Filing</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="agreement">Agreement</MenuItem>
                  <MenuItem value="invoice">Invoice</MenuItem>
                  <MenuItem value="receipt">Receipt</MenuItem>
                  <MenuItem value="id_document">ID Document</MenuItem>
                  <MenuItem value="medical_record">Medical Record</MenuItem>
                  <MenuItem value="police_report">Police Report</MenuItem>
                  <MenuItem value="expert_report">Expert Report</MenuItem>
                  <MenuItem value="witness_statement">Witness Statement</MenuItem>
                  <MenuItem value="photograph">Photograph</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="audio">Audio</MenuItem>
                  <MenuItem value="email_attachment">Email Attachment</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  label="Sort By"
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="size">Size</MenuItem>
                  <MenuItem value="type">Type</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('');
                  setSortBy('name');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Documents Grid/List */}
        {viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {sortedDocuments.map((document) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={document.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onClick={() => onDocumentClick(document)}
                >
                  {getDocumentPreview(document)}
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ mr: 1, width: 24, height: 24 }}>
                        {getDocumentTypeIcon(document.documentType)}
                      </Avatar>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                        {document.title}
                      </Typography>
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                      {document.description}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={document.documentType}
                        color={getDocumentTypeColor(document.documentType) as any}
                        size="small"
                      />
                      {document.isTemplate && (
                        <Chip label="Template" color="info" size="small" />
                      )}
                      {document.isConfidential && (
                        <Chip label="Confidential" color="error" size="small" />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(document.fileSize)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDocumentClick(document);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {!document.isTemplate && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDocumentEdit(document);
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDocumentDownload(document.id, document.originalName);
                            }}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* List View */
          <Box>
            {sortedDocuments.map((document) => (
              <Card
                key={document.id}
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => onDocumentClick(document)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {getDocumentTypeIcon(document.documentType)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {document.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {document.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip
                          label={document.documentType}
                          color={getDocumentTypeColor(document.documentType) as any}
                          size="small"
                        />
                        {document.isTemplate && (
                          <Chip label="Template" color="info" size="small" />
                        )}
                        {document.isConfidential && (
                          <Chip label="Confidential" color="error" size="small" />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(document.fileSize)} • {formatDate(document.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDocumentClick(document);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {!document.isTemplate && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDocumentEdit(document);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDocumentDownload(document.id, document.originalName);
                          }}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {sortedDocuments.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No documents found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search criteria or filters
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentGallery; 